import { Client, type ClientOptions } from "persona";
import { useEffect, useRef, useState } from "react";
import { createPersonaInquiry } from "../api/client";
import {
  BUYER_FUNDING_FLOW_PERSONA_LIMITATION,
  personaClientConfig
} from "../flows/buyerFunding/personaConfig";
import type { BuyerFundingFlowSession } from "../flows/buyerFunding/types";
import { FlowStepPhoneFrame } from "./FlowStepPhoneFrame";

export type PersonaVerificationResult = {
  inquiryId: string;
  status: string;
  mode: "live" | "simulated";
  limitation: string;
};

type Props = {
  session: BuyerFundingFlowSession;
  onVerified: (result: PersonaVerificationResult) => void;
};

function demoRecordingMode(): boolean {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem("buyer-funding-demo-record") === "1";
}

export function PersonaIdentityPanel({ session, onVerified }: Props) {
  const config = personaClientConfig();
  const clientRef = useRef<Client | null>(null);
  const onVerifiedRef = useRef(onVerified);
  onVerifiedRef.current = onVerified;

  const [phase, setPhase] = useState<"idle" | "loading" | "open" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [attempt, setAttempt] = useState(0);
  const forceSimulate = demoRecordingMode();

  useEffect(() => {
    if (!config.isConfigured || forceSimulate) return;

    let cancelled = false;

    async function startPersona() {
      setPhase("loading");
      setErrorMessage("");

      const referenceId = `buyer-funding-${session.fileCode}-${session.buyerLastName}`.toLowerCase();

      let inquiryId: string | undefined;
      try {
        const created = await createPersonaInquiry({ referenceId, fileCode: session.fileCode });
        inquiryId = created.inquiryId;
      } catch {
        if (!import.meta.env.DEV) {
          setPhase("error");
          setErrorMessage("Identity verification could not start. Try again in a moment.");
          return;
        }
      }

      if (cancelled) return;

      const sharedCallbacks: Pick<ClientOptions, "onComplete" | "onCancel" | "onError"> = {
        onComplete: ({ inquiryId: completedId, status }) => {
          onVerifiedRef.current({
            inquiryId: completedId,
            status,
            mode: "live",
            limitation: BUYER_FUNDING_FLOW_PERSONA_LIMITATION
          });
        },
        onCancel: () => {
          setPhase("idle");
          setErrorMessage("Identity verification was cancelled. You can try again.");
        },
        onError: (error) => {
          setPhase("error");
          setErrorMessage(error.message || "Persona could not complete identity verification.");
        }
      };

      let clientOptions: ClientOptions;

      if (inquiryId) {
        clientOptions = {
          inquiryId,
          environmentId: config.environmentId,
          environment: config.environment,
          onReady: () => {
            if (!cancelled) {
              setPhase("open");
              clientRef.current?.open();
            }
          },
          ...sharedCallbacks
        };
      } else if (import.meta.env.DEV && config.templateId) {
        clientOptions = {
          templateId: config.templateId,
          referenceId,
          environmentId: config.environmentId,
          environment: config.environment,
          onReady: () => {
            if (!cancelled) {
              setPhase("open");
              clientRef.current?.open();
            }
          },
          ...sharedCallbacks
        };
      } else {
        setPhase("error");
        setErrorMessage("Identity verification is temporarily unavailable. Please try again.");
        return;
      }

      const client = new Client(clientOptions);
      clientRef.current = client;
    }

    void startPersona();

    return () => {
      cancelled = true;
      clientRef.current?.destroy();
      clientRef.current = null;
    };
  }, [
    config.environment,
    config.environmentId,
    config.isConfigured,
    config.templateId,
    forceSimulate,
    attempt,
    session.buyerLastName,
    session.fileCode
  ]);

  function retryPersona() {
    clientRef.current?.destroy();
    clientRef.current = null;
    setAttempt((current) => current + 1);
  }

  function handleSimulate() {
    onVerifiedRef.current({
      inquiryId: `inq_sim_${session.fileCode.toLowerCase()}`,
      status: "completed",
      mode: "simulated",
      limitation:
        "Simulated Persona identity check for development. Set VITE_PERSONA_TEMPLATE_ID, VITE_PERSONA_ENVIRONMENT_ID (or VITE_PERSONA_ENVIRONMENT), and PERSONA_API_KEY for live Persona."
    });
  }

  return (
    <FlowStepPhoneFrame testId="persona-identity-panel">
          <div className="text-center">
            {config.isConfigured && !forceSimulate ? (
              <div className="space-y-3">
                {phase === "idle" || phase === "loading" ? (
                  <p className="text-sm font-semibold text-[#1f6b57]" data-testid="persona-loading">
                    Opening Persona…
                  </p>
                ) : null}
                {phase === "open" ? (
                  <p className="text-sm text-[#596158]">Complete the steps in Persona.</p>
                ) : null}
                {phase === "error" && errorMessage ? (
                  <p className="text-sm text-[#8a2d10]" data-testid="persona-error">
                    {errorMessage}
                  </p>
                ) : null}
                {phase === "open" || phase === "error" ? (
                  <button className="button-secondary w-full" type="button" onClick={retryPersona}>
                    Try identity verification again
                  </button>
                ) : null}
              </div>
            ) : (
              <button
                className="button-primary w-full"
                data-testid="persona-simulate"
                type="button"
                onClick={handleSimulate}
              >
                Continue demo walkthrough
              </button>
            )}
          </div>
    </FlowStepPhoneFrame>
  );
}
